import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';
import AdminHeader from './AdminHeader';
import ClientHeader from './ClientHeader';
import NavSidebar from './NavSidebar';
import { useView } from '../contexts/ViewContext';

/**
 * DashboardShell — the persistent app chrome (header + sidebar) shared by every
 * "dashboard-style" page (Dashboard, Bookings, Visitors, AI Visibility, MY WORK,
 * the intelligence pages, …).
 *
 * WHY THIS EXISTS
 * Before this layout, every page rendered its OWN copy of <AdminHeader/> +
 * <NavSidebar/>. Because the chrome lived inside each route element, navigating
 * between /my-work, /dashboard, /bookings, /visitors, … unmounted the entire
 * shell and re-mounted a fresh one on the next page. The header re-ran its
 * mount effects (ResizeObserver, unread-count fetch, localStorage reads), the
 * sidebar re-read every feature flag, and the content area flashed white while
 * data refetched — which looked exactly like a full page refresh even though
 * no document reload happened.
 *
 * With this layout the chrome mounts ONCE (here) and only the page content
 * (the <Outlet/>) swaps on navigation, so transitions are seamless.
 *
 * HOW PAGES DECLARE THEIR ACTIVE STATE
 * Each page still owns its own `activePage`/`rightSlot` values (some are
 * dynamic, e.g. ProjectDetailPage's `navActiveKeyForModality`). Instead of
 * passing them to a header it no longer renders, the page calls
 * `usePageMeta({ headerActive, sidebarActive, rightSlot }, [deps])`. The layout
 * reads that meta and feeds it to the (single, persistent) header + sidebar.
 */

const DashboardShellContext = createContext(null);

const DEFAULT_META = {
  headerActive: 'Dashboard',
  sidebarActive: 'home',
  rightSlot: null,
};

/** Read the shell context. Safe to call outside the shell (no-op fallback). */
export function useDashboardShell() {
  const ctx = useContext(DashboardShellContext);
  return ctx || { meta: DEFAULT_META, setPageMeta: () => {} };
}

/**
 * Page → shell declaration. A page calls this to tell the persistent chrome which
 * nav item to highlight and (optionally) what to render in the header right slot.
 *
 * @param {object} meta - `{ headerActive, sidebarActive, rightSlot }`
 * @param {Array}  deps  - values the meta depends on (e.g. a dynamic sidebar key,
 *                        a computed right slot). When these change the meta is
 *                        re-published so the chrome stays in sync.
 */
export function usePageMeta(meta, deps = []) {
  const { setPageMeta } = useDashboardShell();
  useEffect(() => {
    setPageMeta((prev) => ({ ...prev, ...meta }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPageMeta, ...deps]);
}

function readUsername(isClient) {
  try {
    const raw = localStorage.getItem('username');
    if (raw && raw !== 'undefined') return raw;
  } catch {}
  return isClient ? 'client_user' : 'username';
}

function initialsFor(username) {
  if (!username) return '';
  return username
    .split(' ')
    .map((n) => n[0]?.toUpperCase() || '')
    .join('')
    .slice(0, 2);
}

export function DashboardLayout() {
  const [meta, setMeta] = useState(DEFAULT_META);
  // Expose the setter as `setPageMeta` so `usePageMeta` (which destructures that
  // name) lines up with the context value — and so the no-op fallback in
  // useDashboardShell (for pages rendered outside the shell, e.g. in unit
  // tests) keeps the same shape.
  const ctx = useMemo(() => ({ meta, setPageMeta: setMeta }), [meta]);
  const { isClient } = useView();
  const username = readUsername(isClient);
  const initials = initialsFor(username);

  return (
    <DashboardShellContext.Provider value={ctx}>
      <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
        {isClient ? (
          <ClientHeader
            username={username}
            initials={initials}
            activePage={meta.headerActive}
            rightSlot={meta.rightSlot}
          />
        ) : (
          <AdminHeader
            activePage={meta.headerActive}
            rightSlot={meta.rightSlot}
          />
        )}
        <div className="flex">
          <NavSidebar activePage={meta.sidebarActive} />
          <Outlet />
        </div>
      </div>
    </DashboardShellContext.Provider>
  );
}

export default DashboardLayout;
